import CardAboutModal from "@/components/CardAboutModal";
import DiscoverIconSM from "@/components/DiscoverIconSm";
import PlayBox from "@/components/PlayBox";
import ThisIsModal from "@/components/ThisIsModal";
import Thumbnail from "@/components/Thumbnails";
import {
  Card,
  CardBody,
  Center,
  Container,
  Divider,
  HStack,
  Heading,
  Text,
  useColorMode,
} from "@chakra-ui/react";
import { useInView } from "framer-motion";
import { InferGetStaticPropsType } from "next";
import Head from "next/head";
import Image from "next/image";
import Link from "next/link";
import { useRef } from "react";

const metaContent = "Stories from the humans that make it all possible";

interface PostProps {
  title: string;
  content: string;
  slug: string;
}

export default function BlogPost({
  post,
}: InferGetStaticPropsType<typeof getStaticProps>) {
  const { colorMode } = useColorMode();
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });

  return (
    <>
      <Head>
        <title>{post.title}</title>
        <link rel="icon" href="/SC Icon_A_Light.svg" />
        <meta name="description" content={metaContent} />
        <meta property="og:title" content={post.title} />
        <meta property="og:description" content={metaContent} />
        <meta property="og:type" content="article" />
        <meta
          property="og:url"
          content={`https://songcards.io/blog/${post.slug}`}
        />
        <meta property="og:site_name" content="Songcards" />
        {post.image && <meta property="og:image" content={post.image} />}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={post.title} />
        <meta name="twitter:description" content={metaContent} />
        <meta
          name="twitter:url"
          content={`https://songcards.io/blog/${post.slug}`}
        />
        {post.image && <meta name="twitter:image" content={post.image} />}
        <meta name="twitter:site" content="@unlockyoursound" />
        <meta name="twitter:creator" content="@unlockyoursound" />
        <meta name="twitter:image:alt" content={post.title} />
      </Head>

      <Container maxW="lg" p={4} mb={100}>
        {post.image && (
          <Center>
            <Image
              src={post.image}
              alt={post.title}
              width={200}
              height={200}
              unoptimized
              style={{
                width: 200,
                height: 200,
                borderRadius: "50%",
                objectFit: "cover",
                marginBottom: 50,
                borderWidth: 2,
                borderColor: "white",
                borderStyle: "solid",
              }}
            />
          </Center>
        )}
        {post.linked_card && (
          <Center>
            <PlayBox card={post.linked_card} collection={false} />
          </Center>
        )}
        <Divider mt={10} mb={5} />
        <br />
        <Heading>{post.title}</Heading>
        <br />
        <div
          className="blog-post-content"
          dangerouslySetInnerHTML={{ __html: post.content }}
        />
        <Divider mt={10} mb={5} />
        <Center mt={0} mb={5}>
          <Heading as={"h3"} size={"lg"} mt={5} mb={5}>
            Featured Songcard
          </Heading>
        </Center>
        <div ref={ref}>
          <Center
            style={{
              transform: isInView ? "none" : "translateY(+100px)",
              transition: "all 0.9s cubic-bezier(0.17, 0.55, 0.55, 1) 0.5s",
            }}
          >
            <Card
              maxW="xs"
              align="center"
              bgColor={colorMode == "dark" ? "black" : "white"}
              variant="outline"
              borderColor={colorMode == "dark" ? "white" : "black"}
              borderStyle="dotted"
              borderRadius={15}
              shadow={"md"}
            >
              <CardBody>
                <Link href={`/cards/${post.linked_card.uuid}`}>
                  <Thumbnail
                    ext_file={post.linked_card.ext_file}
                    comp_file={post.linked_card.comp_file}
                  />
                </Link>
                <br />
                <Center>
                  <PlayBox card={post.linked_card} collection={false} />
                </Center>
                <Text ml={4} mb={3} mt={5}>
                  is a Songcard from
                  <br />
                  {post.linked_card.primary_artist && (
                    <Text as="b" key={post.linked_card.primary_artist.slug}>
                      <Link
                        href={`/artists/${post.linked_card.primary_artist.slug}`}
                      >
                        {post.linked_card.primary_artist.name}
                      </Link>
                    </Text>
                  )}
                </Text>
                <Divider mb={5} />
                <Center>
                  <HStack justify="space-between" w="94%">
                    <CardAboutModal
                      card={post.linked_card}
                      price={false}
                      showOpen={true}
                    />
                    <ThisIsModal
                      card={post.linked_card}
                      price={false}
                      showOpen={false}
                    />
                  </HStack>
                </Center>
              </CardBody>
            </Card>
          </Center>
        </div>
        <DiscoverIconSM />
      </Container>
    </>
  );
}

export async function getStaticProps({ params }: { params: { slug: string } }) {
  const { slug } = params;

  const res = await fetch(`https://api.songcards.io/blog-post/${slug}/`);
  const blog = await res.json();

  return {
    props: {
      post: blog,
    },
    revalidate: 86400,
  };
}

export async function getStaticPaths() {
  const endpoint = "https://api.songcards.io/blog-post/";

  if (endpoint) {
    const response = await fetch(endpoint);
    const blogs = await response.json();
    const paths = await blogs.map((post: PostProps) => ({
      params: { slug: post.slug },
    }));

    return { paths, fallback: "blocking" };
  }
}
